import React, { useState } from "react";
import { CalendarRange, X } from "lucide-react";

const IST = "Asia/Kolkata";
const pad = (n) => String(n).padStart(2, "0");
const iso = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`;                       // m = 1..12
const lastDay = (y, m) => new Date(y, m, 0).getDate();
const istYM = () => {
  const p = new Intl.DateTimeFormat("en-US", { timeZone: IST, year: "numeric", month: "numeric" }).formatToParts(new Date());
  const get = (t) => Number(p.find((x) => x.type === t).value);
  return { y: get("year"), m: get("month") };
};
const shift = (y, m, k) => { const t = (y * 12 + m - 1) + k; return { y: Math.floor(t / 12), m: (t % 12) + 1 }; };

export const PRESETS = [["this_month", "This month"], ["last_month", "Last month"], ["last_3_months", "Last 3 months"], ["all", "All time"], ["custom", "Custom…"]];

/** Preset → { from, to } as IST calendar days (YYYY-MM-DD, inclusive); `all` → nulls. */
export function presetRange(preset, now = istYM()) {
  const { y, m } = now;
  if (preset === "this_month") return { from: iso(y, m, 1), to: iso(y, m, lastDay(y, m)) };
  if (preset === "last_month") { const p = shift(y, m, -1); return { from: iso(p.y, p.m, 1), to: iso(p.y, p.m, lastDay(p.y, p.m)) }; }
  if (preset === "last_3_months") { const p = shift(y, m, -2); return { from: iso(p.y, p.m, 1), to: iso(y, m, lastDay(y, m)) }; }
  return { from: null, to: null };
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtDay = (d, withYear) => { const [y, m, day] = d.split("-").map(Number); return `${day} ${MONTHS[m - 1]}${withYear ? ` ${y}` : ""}`; };
/** "1 Sep – 30 Sep" (year shown only when it differs from the current IST year). */
export function rangeLabel(from, to, year = istYM().y) {
  if (!from && !to) return null;
  const wy = (d) => d && Number(d.slice(0, 4)) !== year;
  if (from && to) return `${fmtDay(from, wy(from))} – ${fmtDay(to, wy(to))}`;
  return from ? `From ${fmtDay(from, wy(from))}` : `Until ${fmtDay(to, wy(to))}`;
}

const inputCls = "h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs font-semibold text-[#0A2540]";

/** Compact date filter: preset select; Custom reveals From/To + Apply. Only presets and Apply call `onApply`. */
export function DateFilter({ preset, from, to, onApply }) {
  const [draft, setDraft] = useState({ from: from || "", to: to || "" });
  const custom = preset === "custom";
  const label = rangeLabel(from, to);
  const pick = (p) => (p === "custom" ? onApply({ preset: "custom", from, to, pending: true }) : onApply({ preset: p, ...presetRange(p) }));
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="earn-date-filter">
      <label className="inline-flex items-center gap-1.5 text-xs text-slate-500">
        <CalendarRange size={13} className="text-slate-400" />
        <select value={preset} onChange={(e) => pick(e.target.value)} className={inputCls} data-testid="earn-date-preset" aria-label="Date range">
          {PRESETS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </label>
      {custom && (
        <form className="inline-flex flex-wrap items-center gap-1.5" data-testid="earn-date-custom"
              onSubmit={(e) => { e.preventDefault(); onApply({ preset: "custom", from: draft.from || null, to: draft.to || null }); }}>
          <input type="date" value={draft.from} max={draft.to || undefined} onChange={(e) => setDraft({ ...draft, from: e.target.value })} className={inputCls} data-testid="earn-date-from" aria-label="From" />
          <span className="text-slate-400 text-xs">–</span>
          <input type="date" value={draft.to} min={draft.from || undefined} onChange={(e) => setDraft({ ...draft, to: e.target.value })} className={inputCls} data-testid="earn-date-to" aria-label="To" />
          <button type="submit" disabled={!draft.from && !draft.to} data-testid="earn-date-apply"
                  className="h-8 rounded-lg bg-[#0A2540] text-white text-xs font-bold px-3 disabled:opacity-40">Apply</button>
        </form>
      )}
      {label && (
        <span className="inline-flex items-center gap-1 rounded-full bg-[#0A2540] text-white text-[11px] font-bold px-2.5 py-1" data-testid="earn-date-active">
          {label}
          <button type="button" onClick={() => onApply({ preset: "all", from: null, to: null })} aria-label="Clear date range" data-testid="earn-date-clear" className="rounded-full hover:bg-white/20 p-0.5"><X size={11} /></button>
        </span>
      )}
    </div>
  );
}
